/**
 * User Profile Hook
 *
 * Custom React hook for managing user profile state and operations.
 * Uses Zustand store for centralized state management and respects
 * MFA requirements to prevent infinite API call loops.
 *
 * @module hooks/useUserProfile
 */

import { useEffect, useCallback, useRef } from 'react';
import { useUserProfileStore } from '@/stores/userProfileStore';
import { useMfaStore } from '@/stores/mfaStore';
import { UserApiError } from '@/services/domains/UserDomain';
import type { UserProfile, UserRole } from '@/types/user';

/**
 * User profile hook state interface
 */
export interface UseUserProfileState {
  /** Current user profile data */
  profile: UserProfile | null;
  /** Loading state for profile operations */
  isLoading: boolean;
  /** Error state for profile operations */
  error: UserApiError | null;
  /** Whether profile data has been loaded at least once */
  isInitialized: boolean;
  /** Whether a refresh operation is in progress */
  isRefreshing: boolean;
  /** Whether an update operation is in progress */
  isUpdating: boolean;
}

/**
 * User profile hook actions interface
 */
export interface UseUserProfileActions {
  /** Fetch current user profile */
  fetchProfile: () => Promise<void>;
  /** Refresh profile data with retry mechanism */
  refreshProfile: (maxRetries?: number) => Promise<void>;
  /** Update user profile */
  updateProfile: (profileData: Partial<UserProfile>) => Promise<void>;
  /** Clear error state */
  clearError: () => void;
  /** Reset all state to initial values */
  reset: () => void;
  /** Check if user has specific role */
  hasRole: (role: UserRole) => boolean;
}

/**
 * User profile hook return type
 */
export interface UseUserProfileReturn extends UseUserProfileState, UseUserProfileActions {}

/**
 * Hook options interface
 */
export interface UseUserProfileOptions {
  /** Whether to automatically fetch profile on mount */
  autoFetch?: boolean;
  /** Callback when profile is successfully loaded */
  onProfileLoaded?: (profile: UserProfile) => void;
  /** Callback when profile update is successful */
  onProfileUpdated?: (profile: UserProfile) => void;
  /** Callback when an error occurs */
  onError?: (error: UserApiError) => void;
}

/**
 * Custom hook for user profile management
 *
 * Uses Zustand store for centralized state management.
 * Respects MFA requirements - won't fetch when MFA is required.
 *
 * @param options Configuration options for the hook
 * @returns User profile state and actions
 */
export function useUserProfile(options: UseUserProfileOptions = {}): UseUserProfileReturn {
  const {
    autoFetch = true,
    onProfileLoaded,
    onProfileUpdated,
    onError,
  } = options;

  // Get state from Zustand store
  const profile = useUserProfileStore((state) => state.profile);
  const status = useUserProfileStore((state) => state.status);
  const error = useUserProfileStore((state) => state.error);
  const isInitialized = useUserProfileStore((state) => state.isInitialized);

  // Get MFA state to check if we should skip fetching
  const mfaRequired = useMfaStore((state) => state.mfaRequired);

  // Store actions
  const storeFetchProfile = useUserProfileStore((state) => state.fetchProfile);
  const storeUpdateProfile = useUserProfileStore((state) => state.updateProfile);
  const storeClearError = useUserProfileStore((state) => state.clearError);
  const storeReset = useUserProfileStore((state) => state.reset);
  const storeHasRole = useUserProfileStore((state) => state.hasRole);

  // Refs for callbacks and preventing stale closures
  const onProfileLoadedRef = useRef(onProfileLoaded);
  const onProfileUpdatedRef = useRef(onProfileUpdated);
  const onErrorRef = useRef(onError);
  const previousProfileRef = useRef(profile);

  // Keep refs updated
  useEffect(() => {
    onProfileLoadedRef.current = onProfileLoaded;
    onProfileUpdatedRef.current = onProfileUpdated;
    onErrorRef.current = onError;
  }, [onProfileLoaded, onProfileUpdated, onError]);

  // Call onProfileLoaded when profile changes from null to a value
  useEffect(() => {
    if (profile && !previousProfileRef.current && onProfileLoadedRef.current) {
      onProfileLoadedRef.current(profile);
    }
    previousProfileRef.current = profile;
  }, [profile]);

  // Call onError when error changes
  useEffect(() => {
    if (error && onErrorRef.current) {
      onErrorRef.current(error);
    }
  }, [error]);

  /**
   * Fetch current user profile
   */
  const fetchProfile = useCallback(async (): Promise<void> => {
    // Don't fetch if MFA is required - redirect will handle it
    if (mfaRequired) {
      return;
    }
    await storeFetchProfile();
  }, [mfaRequired, storeFetchProfile]);

  /**
   * Refresh profile data (same as fetch for Zustand implementation)
   */
  const refreshProfile = useCallback(async (): Promise<void> => {
    // Don't refresh if MFA is required
    if (mfaRequired) {
      return;
    }
    await storeFetchProfile();
  }, [mfaRequired, storeFetchProfile]);

  /**
   * Update user profile
   */
  const updateProfile = useCallback(async (profileData: Partial<UserProfile>): Promise<void> => {
    // Don't update if MFA is required
    if (mfaRequired) {
      return;
    }

    const previousProfile = useUserProfileStore.getState().profile;
    await storeUpdateProfile(profileData);

    // Call callback if update was successful
    const newProfile = useUserProfileStore.getState().profile;
    if (newProfile && newProfile !== previousProfile && onProfileUpdatedRef.current) {
      onProfileUpdatedRef.current(newProfile);
    }
  }, [mfaRequired, storeUpdateProfile]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    storeClearError();
  }, [storeClearError]);

  /**
   * Reset all state to initial values
   */
  const reset = useCallback(() => {
    storeReset();
  }, [storeReset]);

  /**
   * Check if user has specific role
   */
  const hasRole = useCallback((role: UserRole): boolean => {
    return storeHasRole(role);
  }, [storeHasRole]);

  // Auto-fetch profile on mount (only if not already initialized and MFA not required)
  useEffect(() => {
    if (autoFetch && !isInitialized && status !== 'loading' && !mfaRequired) {
      storeFetchProfile();
    }
  }, [autoFetch, isInitialized, status, mfaRequired, storeFetchProfile]);

  return {
    // State - derived from Zustand store
    profile,
    isLoading: status === 'loading',
    error,
    isInitialized,
    isRefreshing: status === 'loading', // Same as loading in Zustand impl
    isUpdating: status === 'loading', // Same as loading in Zustand impl

    // Actions
    fetchProfile,
    refreshProfile,
    updateProfile,
    clearError,
    reset,
    hasRole,
  };
}
